import { useCallback, useEffect, useMemo, useState } from 'react';
import _map from 'lodash/map';
import { isItemsMatch } from '@lib/components/Table/utils';
import { BaseItem } from '@lib/components/Table/types';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';

export interface UseTableItemsToggleColumnOptions<T extends BaseItem> {
  /** Whether the column is interactive at all. When false every toggle is disabled. */
  enabled?: boolean;
  /** Current page rows. Used to prune stale visibility entries when rows are un-selected on the visible page. */
  data: T[];
  /** Bulk-select result driving which rows are currently "selected" (=> linkable). */
  useBulkSelectResult?: UseBulkSelectResult<T>;
  /** Optional default value for rows that have no entry in the local map yet. */
  defaultValue?: boolean;
  /**
   * Returns the canonical toggle value for a row when known up-front (e.g. from prefetched
   * link data once the BE exposes it). Falls back to {@link defaultValue} when not provided.
   */
  getInitialValue?: (item: T) => boolean | undefined;
  /**
   * Resolves every bulk-selected id when the user has clicked "select all". Required for the
   * header toggle's set-all action when select-all expansion is in play.
   */
  fetchAllSelectedIds?: () => Promise<string[]> | undefined;
  /** Commits a single-row toggle change (in edit-mode usually fires the parent update mutation). */
  onCommitRow?: (item: T, value: boolean) => void;
  /** Commits a multi-row toggle change (header set-all). Called once per id. */
  onCommitMany?: (ids: string[], value: boolean) => void;
}

export interface UseToggleColumnResult<T extends BaseItem> {
  /** Whether the synthetic column should render at all. */
  isVisible: boolean;
  /** Header toggle aggregate state across every bulk-selected row, including off-page selections. */
  headerToggleChecked: boolean;
  /** Header toggle disabled when no rows are bulk-selected, or column is disabled wholesale. */
  headerToggleDisabled: boolean;
  /** Set-all handler; uses fetchAllSelectedIds when "select all" is active. */
  onHeaderToggleChange: (next: boolean) => void;
  /** Per-row checked state lookup. */
  getRowToggleChecked: (item: T) => boolean;
  /** Per-row disabled state lookup; true when row isn't bulk-selected or column is disabled. */
  getRowToggleDisabled: (item: T) => boolean;
  /** Per-row commit handler. */
  onRowToggleChange: (item: T, next: boolean) => void;
  /** Local visibility map keyed by row id. Lets the host form persist values across modal close/open. */
  visibilityMap: Record<string, boolean>;
  /** Replace the local visibility map (e.g. when restoring create-mode state on modal open). */
  setVisibilityMap: (next: Record<string, boolean>) => void;
}

/**
 * Drives a generic bulk-select-gated toggle column for picker tables.
 *
 * The hook is intentionally pure: it never fires GraphQL mutations itself. Callers wire either
 * `onCommitRow` / `onCommitMany` to live mutations (edit-mode) or to a local form-state setter
 * (create-mode). State is held locally so toggling does not depend on a read-side field that
 * the BE may not yet expose.
 */
export default function useTableItemsToggleColumn<T extends BaseItem>(
  options: UseTableItemsToggleColumnOptions<T>,
): UseToggleColumnResult<T> {
  const {
    enabled = true,
    data,
    useBulkSelectResult,
    defaultValue = true,
    getInitialValue,
    fetchAllSelectedIds,
    onCommitRow,
    onCommitMany,
  } = options;

  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>(
    {},
  );

  const isRowBulkSelected = useCallback(
    (item: T): boolean => {
      if (!useBulkSelectResult) return false;
      const {
        isAllTableItemsSelected,
        selectedTableItems = [],
        unselectedTableItems = [],
      } = useBulkSelectResult;
      if (isAllTableItemsSelected) {
        return !unselectedTableItems.some((unselected) =>
          isItemsMatch(unselected, item),
        );
      }
      return selectedTableItems.some((selected) =>
        isItemsMatch(selected, item),
      );
    },
    [useBulkSelectResult],
  );

  // Prune visibilityMap entries for rows that are visible on the current page
  // but no longer bulk-selected. This way re-selecting a previously-toggled
  // row falls back to `getInitialValue` (BE state) instead of the stale
  // session toggle. Rows not in `data` (e.g. on other pages) are left
  // untouched so cross-page toggles survive pagination.
  useEffect(() => {
    setVisibilityMap((prev) => {
      let mutated = false;
      const next: Record<string, boolean> = { ...prev };
      data.forEach((item) => {
        const id = String(item.id);
        if (id in next && !isRowBulkSelected(item)) {
          delete next[id];
          mutated = true;
        }
      });
      return mutated ? next : prev;
    });
  }, [data, isRowBulkSelected]);

  const resolveValue = useCallback(
    (item: T): boolean => {
      const id = String(item.id);
      if (id in visibilityMap) return visibilityMap[id];
      const initial = getInitialValue?.(item);
      return initial ?? defaultValue;
    },
    [visibilityMap, getInitialValue, defaultValue],
  );

  // Aggregate over every currently-selected item, not just the current page —
  // the header write path already applies to all selections (and "select all"
  // expands via `fetchAllSelectedIds`), so reading from on-page rows alone made
  // the header state lie as soon as the user paginated.
  const allBulkSelectedItems = useMemo(() => {
    if (!useBulkSelectResult) return [] as T[];
    const { isAllTableItemsSelected, selectedTableItems = [] } =
      useBulkSelectResult;
    // Unmaterialized "select all" mode (no `fetchIds` upstream): we don't have
    // every id in memory, so fall back to the visible page rather than reporting
    // "false" unconditionally.
    if (isAllTableItemsSelected && selectedTableItems.length === 0) {
      return data.filter((item) => isRowBulkSelected(item));
    }
    return selectedTableItems;
  }, [useBulkSelectResult, data, isRowBulkSelected]);

  const headerToggleChecked = useMemo(() => {
    if (allBulkSelectedItems.length === 0) return false;
    return allBulkSelectedItems.every((item) => resolveValue(item));
  }, [allBulkSelectedItems, resolveValue]);

  const hasAnyBulkSelected = useMemo(() => {
    if (!useBulkSelectResult) return false;
    const { isAllTableItemsSelected, selectedTableItems = [] } =
      useBulkSelectResult;
    return isAllTableItemsSelected || selectedTableItems.length > 0;
  }, [useBulkSelectResult]);

  const headerToggleDisabled = !enabled || !hasAnyBulkSelected;

  const onRowToggleChange = useCallback(
    (item: T, next: boolean) => {
      const id = String(item.id);
      setVisibilityMap((prev) => ({ ...prev, [id]: next }));
      onCommitRow?.(item, next);
    },
    [onCommitRow],
  );

  const onHeaderToggleChange = useCallback(
    (next: boolean) => {
      if (!useBulkSelectResult) return;
      const {
        isAllTableItemsSelected,
        selectedTableItems = [],
        unselectedTableItems = [],
      } = useBulkSelectResult;

      const applyIds = (ids: string[]) => {
        if (!ids.length) return;
        setVisibilityMap((prev) => {
          const updated = { ...prev };
          ids.forEach((id) => {
            updated[id] = next;
          });
          return updated;
        });
        onCommitMany?.(ids, next);
      };

      if (!isAllTableItemsSelected) {
        applyIds(_map(selectedTableItems, 'id') as string[]);
        return;
      }

      // "Select all" mode: expand via fetchAllSelectedIds, minus opt-outs.
      const result = fetchAllSelectedIds?.();
      if (!result) return;
      result
        .then((allIds) => {
          if (!allIds?.length) return;
          const optOutIds = new Set(
            unselectedTableItems.map((item) => String(item.id)),
          );
          applyIds(allIds.filter((id) => !optOutIds.has(String(id))));
        })
        .catch(() => undefined);
    },
    [useBulkSelectResult, fetchAllSelectedIds, onCommitMany],
  );

  const getRowToggleChecked = useCallback(
    (item: T) => resolveValue(item),
    [resolveValue],
  );

  const getRowToggleDisabled = useCallback(
    (item: T) => !enabled || !isRowBulkSelected(item),
    [enabled, isRowBulkSelected],
  );

  return useMemo(
    () => ({
      isVisible: true,
      headerToggleChecked,
      headerToggleDisabled,
      onHeaderToggleChange,
      getRowToggleChecked,
      getRowToggleDisabled,
      onRowToggleChange,
      visibilityMap,
      setVisibilityMap,
    }),
    [
      headerToggleChecked,
      headerToggleDisabled,
      onHeaderToggleChange,
      getRowToggleChecked,
      getRowToggleDisabled,
      onRowToggleChange,
      visibilityMap,
    ],
  );
}
